import * as React from 'react';
import { isDefined, noop } from 'react-financial-charts';
import { saveNodeType } from 'react-financial-charts';
import {
  HoverTextNearMouse,
  Text,
  MouseLocationIndicator,
} from '@react-financial-charts/interactive/lib/components/index';
import { ClickableCircle, EachElliottImpulse, InteractiveStraightLine } from './Wrapper';

import { renderXABCDDottedLines } from './XABCDDottedLines';
import type {
  ChartId,
  ElliottCorrectionABCRenderNode,
  ElliottImpulseRenderNode,
  ElliottImpulseSharedState,
  HoverTextConfig,
  MoreProps,
  MultiPointDragOverride,
  PathRenderNode,
  XABCDPatternRenderNode,
} from '@/types/commontypes';
import { ShapeType } from '@/types/commontypes';
import { getLabelText } from '@/utils/combinedText';
import {
  toElliottCorrectionRFCProps,
  toElloitImpulseRFCProps,
  toPathRFCProps,
  toXabcdRFCProps,
  RFC_RENDER_MODE,
} from '@/utils/shapePropsToAppearance';
import { getActiveTemplateNode } from '@/utils/useActiveShapeProps';

import {
  applyYClampIfNeeded,
  completeOrExtendPath,
  getImpulsePointLimit,
  handleDoubleClick,
  handleDrawLine,
  handleHover,
  handleSelect,
} from '@/utils/impulseUtils';
import { ChartShapesContext } from '../../../contexts/ChartShapesContext';

/**
 * ElliottImpulse 도형 노드 타입.
 * RenderNode의 구조적 서브타입으로 Elliott 계열 도형에 특화된 필드를 required로 좁힌다.
 * TypeScript의 Omit + 인덱스 시그니처 제한으로 extends 대신 명시적 인터페이스로 정의하며,
 * [key: string]: unknown 을 포함해 RenderNode와 구조적 호환성을 유지한다.
 */
export interface ElliottImpulseItem {
  uuid: string;
  type: ShapeType;
  chartId: ChartId;
  points: [number, number][];
  selected: boolean;
  locked: boolean;
  hidden: boolean;
  createdAt: number;
  updatedAt: number;
  [key: string]: unknown;
}

export interface ElliottImpulseProps {
  readonly snap: boolean;
  readonly enabled: boolean;
  readonly snapTo?: (datum: unknown) => number | number[];
  readonly shouldDisableSnap?: (e: React.MouseEvent) => boolean;
  readonly onStart: (e: React.MouseEvent, xyValue: unknown, moreProps: MoreProps) => void;
  readonly onComplete?: (e: React.MouseEvent, elliottImpulses: unknown, moreProps: MoreProps) => void;
  readonly onDragComplete: (e: React.MouseEvent, newTrends: unknown[], moreProps: MoreProps) => void;
  readonly onSelect?: (e: React.MouseEvent, interactives: unknown[], moreProps: MoreProps) => void;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly currentPositionStroke?: string;
  readonly currentPositionStrokeWidth?: number;
  readonly currentPositionstrokeOpacity?: number;
  readonly currentPositionRadius?: number;
  readonly type: ShapeType;
  hoverText?: Partial<HoverTextConfig>;
  readonly elliottImpulses: ElliottImpulseItem[];
  readonly hoverId: string | null;
  readonly onHover?: (hoveredId: string | number | null, moreProps?: MoreProps) => void;
  readonly chartId?: ChartId;
  readonly sharedState?: ElliottImpulseSharedState;
  readonly setSharedState?: React.Dispatch<React.SetStateAction<ElliottImpulseSharedState>>;
  readonly completePath?: boolean;
  readonly isAnyShapeEnabled?: boolean;
}

interface ElliottImpulseState {
  override?: MultiPointDragOverride;
  elliottImpulses?: ElliottImpulseItem[];
}

export class ElliottImpulse extends React.Component<ElliottImpulseProps, ElliottImpulseState> {
  public static contextType = ChartShapesContext;
  declare context: React.ContextType<typeof ChartShapesContext>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static _cachedDefaultProps: Record<string, any> | null = null;

  static get defaultProps() {
    if (!ElliottImpulse._cachedDefaultProps) {
      ElliottImpulse._cachedDefaultProps = {
        onStart: noop,
        onSelect: noop,
        currentPositionStroke: '#1e53e6',
        currentPositionstrokeOpacity: 1,
        currentPositionStrokeWidth: 1,
        currentPositionRadius: 0,
        shouldDisableSnap: (e: React.MouseEvent) => e.button === 2 || e.shiftKey,
        hoverText: {
          ...HoverTextNearMouse.defaultProps,
          enable: true,
          bgHeight: 'auto',
          bgWidth: 'auto',
          text: 'Click to select object',
          selectedText: '',
        },
        elliottImpulses: [],
      };
    }
    return ElliottImpulse._cachedDefaultProps;
  }

  private saveNodeType: <T>(type: string | number) => React.Ref<T>;
  private suppressNextDoubleClick: boolean = false;

  public constructor(props: ElliottImpulseProps) {
    super(props);
    this.saveNodeType = saveNodeType.bind(this);

    this.state = {};
  }

  public render() {
    const {
      type,
      enabled,
      hoverText,
      elliottImpulses,
      currentPositionstrokeOpacity,
      currentPositionRadius = ElliottImpulse.defaultProps.currentPositionRadius,
      currentPositionStroke,
      currentPositionStrokeWidth,
      shouldDisableSnap,
      snap,
      snapTo,
      sharedState,
      chartId,
      isAnyShapeEnabled,
    } = this.props;

    const rfcMode = RFC_RENDER_MODE[type];

    const getAppearance = (node: unknown) => {
      switch (type) {
        case ShapeType.ELLIOTT_IMPULSE_12345:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          return toElloitImpulseRFCProps(node as ElliottImpulseRenderNode);
        case ShapeType.ELLIOTT_CORRECTION_ABC:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          return toElliottCorrectionRFCProps(node as ElliottCorrectionABCRenderNode) as unknown as ReturnType<
            typeof toElloitImpulseRFCProps
          >;
        case ShapeType.XABCD_PATTERN:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          return toXabcdRFCProps(node as XABCDPatternRenderNode) as unknown as ReturnType<
            typeof toElloitImpulseRFCProps
          >;
        case ShapeType.PATH:
        default:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          return toPathRFCProps(node as PathRenderNode) as unknown as ReturnType<typeof toElloitImpulseRFCProps>;
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const activeNode = getActiveTemplateNode(type) as ElliottImpulseRenderNode;
    const appearance = getAppearance(activeNode);
    const { override } = this.state;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
    const { currentPoints, status, endXY } = sharedState ?? ({} as ElliottImpulseSharedState);
    const elliottndLimit = 6;
    const tempCondition =
      isDefined(currentPoints) &&
      isDefined(endXY) &&
      currentPoints.length > 0 &&
      currentPoints.length < elliottndLimit &&
      sharedState?.type === type &&
      chartId === sharedState?.chartId &&
      status !== 'completed' &&
      enabled;

    const tempLine = tempCondition ? (
      <>
        {/* Draw temp straight line to mouse */}
        <InteractiveStraightLine
          ref={this.saveNodeType('dotted-line')}
          x1Value={currentPoints[currentPoints.length - 1][0]}
          y1Value={currentPoints[currentPoints.length - 1][1]}
          x2Value={endXY[0]}
          y2Value={endXY[1]}
          strokeStyle={appearance.strokeStyle}
          strokeWidth={appearance.strokeWidth}
          strokeDasharray={appearance.strokeDasharray}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          type={rfcMode as 'XLINE' | 'RAY' | 'LINE'}
        />

        {/* Render dotted lines dynamically for XABCD */}
        {type === ShapeType.XABCD_PATTERN &&
          renderXABCDDottedLines(
            [...currentPoints, endXY], // include temp point
            appearance.strokeStyle,
            appearance.strokeWidth,
            {
              fill: appearance.textFill!,
              fontSize: appearance.fontSize,
              fontFamily: appearance.fontFamily,
              fontWeight: appearance.fontWeight!,
              fontStyle: appearance?.fontStyle ?? 'normal',
            },
            appearance.fillStyle!,
            'temp',
            rfcMode,
          )}
      </>
    ) : null;

    return (
      <g>
        {/* Circles */}
        {elliottImpulses.map((impulse, index) => {
          const eachAppearance = getAppearance(impulse);
          if (impulse.hidden) return null;
          const hoverTextWithDefault = {
            ...ElliottImpulse.defaultProps.hoverText,
            ...hoverText,
          };

          let points = impulse.points;
          if (override && override.uuid === impulse.uuid) {
            if (isDefined(override.newPoints)) {
              points = override.newPoints;
            } else if (isDefined(override.pointIdx) && isDefined(override.newPoint)) {
              const pIdx = override.pointIdx;
              const pNew = override.newPoint;
              points = impulse.points.map((pt: [number, number], i: number) => (i === pIdx ? pNew : pt));
            }
          }
          const selected = impulse.selected && sharedState?.status !== 'started';
          return (
            <EachElliottImpulse
              key={impulse.uuid}
              ref={this.saveNodeType(index)}
              index={index}
              uuid={impulse.uuid}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              type={RFC_RENDER_MODE[impulse.type] as 'XLINE' | 'RAY' | 'LINE'}
              shapeType={impulse.type}
              selected={selected}
              points={points}
              appearance={eachAppearance}
              onDrag={this.handleDrag}
              onDragComplete={this.handleDragComplete}
              edgeInteractiveCursor="react-financial-charts-move-cursor"
              lineInteractiveCursor="react-financial-charts-pointer-cursor"
              hoverText={hoverTextWithDefault}
              onSelect={(e: React.MouseEvent<Element, MouseEvent>, _uuid: string, moreProps: MoreProps) =>
                handleSelect(e, impulse.uuid, moreProps, this.props)
              }
              onDragStart={(uuid: string) => this.setState({ override: { uuid } })}
              onHover={(id: string | number | null, moreProps?: MoreProps) => handleHover(id, moreProps, this.props)}
              locked={impulse.locked}
              onDoubleClick={(e: React.MouseEvent, mp: MoreProps) => {
                if (this.suppressNextDoubleClick) {
                  this.suppressNextDoubleClick = false; // Reset it
                  e.stopPropagation(); // optional: also stops bubbling
                  return;
                }

                handleDoubleClick(e, mp, this.props);
              }}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              sharedState={(sharedState ?? {}) as ElliottImpulseSharedState}
              isAnyShapeEnabled={isAnyShapeEnabled}
            />
          );
        })}
        {currentPoints &&
          currentPoints.length > 0 &&
          currentPoints.length < getImpulsePointLimit(type) &&
          tempCondition && (
            <g>
              {currentPoints.length > 1 &&
                currentPoints.slice(0, -1).map((start: [number, number], idx: number) => {
                  const end = currentPoints[idx + 1];
                  return (
                    <InteractiveStraightLine
                      key={`current-line-${idx}`}
                      x1Value={start[0]}
                      y1Value={start[1]}
                      x2Value={end[0]}
                      y2Value={end[1]}
                      strokeStyle={appearance.strokeStyle}
                      strokeWidth={appearance.strokeWidth}
                      strokeDasharray={appearance.strokeDasharray}
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                      type={rfcMode as 'XLINE' | 'RAY' | 'LINE'}
                    />
                  );
                })}
              {currentPoints?.map(([x, y]: [number, number], idx: number) => (
                <g key={`currentGroup-${idx}`}>
                  <ClickableCircle
                    key={`current-circle-${idx}`}
                    cx={x}
                    cy={y}
                    r={appearance.r}
                    fillStyle={appearance.edgeFill}
                    strokeStyle={appearance.edgeStroke}
                    strokeWidth={appearance.edgeStrokeWidth}
                    show={true}
                  />
                  {tempCondition && (
                    <Text
                      key={`label-${idx}`}
                      fontFamily={appearance.fontFamily}
                      fontSize={appearance.fontSize}
                      fillStyle={appearance.strokeStyle}
                      xyProvider={({ xScale, chartConfig }: MoreProps) => {
                        const { yScale } = chartConfig;
                        const xPos = xScale(x);
                        const yPos = yScale(y);
                        return [xPos, yPos - 10];
                      }}
                    >
                      {`(${getLabelText(idx, sharedState.type)})`}
                    </Text>
                  )}
                </g>
              ))}
            </g>
          )}
        {tempLine}

        {/* Click listener */}
        <MouseLocationIndicator
          enabled={enabled}
          snap={snap}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          opacity={currentPositionstrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          onMouseMove={(e: React.MouseEvent, xy: unknown, moreProps: MoreProps) => {
            handleDrawLine(e, xy, moreProps, this.props);
          }}
        />
      </g>
    );
  }

  private readonly handleStart = (e: React.MouseEvent, xyValue: unknown, moreProps: MoreProps) => {
    const { sharedState } = this.props;
    if (!isDefined(xyValue)) return;

    const { onStart } = this.props;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
    const { status, currentPoints } = sharedState ?? ({} as ElliottImpulseSharedState);

    if (!currentPoints?.length && status !== 'started') {
      onStart?.(e, [xyValue], moreProps);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const xy = xyValue as [number, number];
    const yValue = applyYClampIfNeeded(xy, moreProps, sharedState);
    this.handleEnd(e, [xy[0], yValue], moreProps);
  };

  private readonly handleEnd = (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => {
    const { sharedState, type, setSharedState } = this.props;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
    const { currentPoints, endXY } = sharedState ?? ({} as ElliottImpulseSharedState);

    if (!this.props.enabled || !isDefined(currentPoints)) return;

    const updatedPoints = [...currentPoints, xyValue];

    const pointLimit = getImpulsePointLimit(type);
    const shouldComplete = updatedPoints.length === pointLimit && isDefined(endXY);

    if (shouldComplete) {
      const newImpulse = completeOrExtendPath(updatedPoints, this.props);
      this.setState({ elliottImpulses: newImpulse }, () => {
        this.props.onComplete?.(e, newImpulse, moreProps);
      });
    } else {
      setSharedState?.({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        ...(sharedState as ElliottImpulseSharedState),
        currentPoints: updatedPoints,
      });
    }
  };

  private readonly handleDragComplete = (e: React.MouseEvent) => {
    const { override } = this.state;
    const { elliottImpulses, onDragComplete, hoverId } = this.props;

    if (isDefined(override) && override.uuid === hoverId) {
      const updatedImpulses = elliottImpulses.map(impulse => {
        if (impulse.uuid !== override.uuid) {
          return { ...impulse, selected: false };
        }
        let updatedPoints;
        if ('newPoints' in override) {
          updatedPoints = override.newPoints;
        } else {
          updatedPoints = impulse.points.map((pt: [number, number], idx: number) =>
            idx === override.pointIdx ? override.newPoint : pt,
          );
        }

        return {
          ...impulse,
          points: updatedPoints,
          updatedAt: Date.now(),
          selected: true,
        };
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      onDragComplete?.(e, updatedImpulses, {} as unknown as MoreProps);
      this.setState({ override: undefined });
    }
  };

  private readonly handleDrag = (
    _e: React.MouseEvent,
    uuid: string,
    pointIdx: number,
    newPoint: [number, number] | [number, number][],
  ) => {
    const { hoverId } = this.props;
    const { override } = this.state;
    if (isDefined(override)) {
      if (override.uuid !== uuid || override.uuid !== hoverId) {
        return;
      }
    }
    if (pointIdx === -1 && Array.isArray(newPoint)) {
      this.setState({
        override: {
          uuid,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          newPoints: newPoint as [number, number][],
        },
      });
    } else {
      this.setState({
        override: {
          uuid,
          pointIdx,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          newPoint: newPoint as [number, number],
        },
      });
    }
  };
}
